<template>
  <div>
    <div
      class="modal-close position-absolute cursor-hand p-1 d-inline-block"
      @click.stop="$emit('close')"
    >
      <Icon name="close" width="14" fill="$scss.heraMuted" />
    </div>
    <Panel
      class="w-100 position-relative d-flex flex-column h-100 suggested-tournaments--invites overflow-hidden"
    >
      <div class="row mt-2 ">
        <div class="col">
          <Title
            :text="
              $t(
                '_web_competitions_my-tournaments-list-title',
                'My Tournaments'
              )
            "
          />
        </div>
      </div>
      <div class="row action-wrapper d-flex justify-content-between">
        <div class="col">
          <SearchInput
            v-model="tournamentSearchString"
            :debounce="700"
            :placeholder="
              $t(
                '_web_competitions_tournaments-list-search-placeholder',
                'Search tournament'
              )
            "
            class="col-7 mx-0 px-0"
            @input="onFilterChange"
          />
        </div>
      </div>
      <div
        class="row py-0 flex-shrink-1 tournament-list-wrapper position-relative custom-scrollbar"
      >
        <div class="col">
          <div class="tournament-list w-100">
            <infiniteScroll
              :list="tournaments"
              :is-disabled="isLoading || nextPageToken === null"
              :activation-gap="100"
              list-name="tournaments"
              in-container
              :container-element="$refs.scrollableRef"
              @ScrollToBottom="fetchTournaments"
            >
              <table
                id="tournaments-list"
                slot-scope="{}"
                class="table table-hover hera-table m-0 px-0"
              >
                <caption
                  v-if="filteredTournaments.length === 0 && isLoading"
                  class="text-center text-muted"
                >
                  <TextBlock :size="0.75" variant="secondary">{{
                    $t(
                      '_web_competitions_tournaments-list-loading-state',
                      'Fetching tournaments...'
                    )
                  }}</TextBlock>
                </caption>
                <caption
                  v-else-if="filteredTournaments.length === 0"
                  class="text-center text-muted"
                >
                  <TextBlock :size="0.75" variant="secondary">{{
                    $t(
                      '_web_competitions_tournaments-list-empty-state',
                      'No tournaments to show'
                    )
                  }}</TextBlock>
                </caption>
                <template v-else>
                  <thead>
                    <tr>
                      <th
                        v-for="(column, index) in columns"
                        :key="column.id"
                        class="sticky-top"
                        :class="index === 0 ? 'h-pr-2' : 'px-2'"
                        @click="sort(column.id)"
                      >
                        <TextBlock
                          :size="0.75"
                          :variant="
                            sorting.columnId === column.id
                              ? 'primary'
                              : 'secondary'
                          "
                        >
                          {{
                            $t(
                              `_web_competitions_tournaments-list-${
                                column.id
                              }-col-title`,
                              column.label
                            )
                          }}
                        </TextBlock>
                        <Icon :name="columnIconName(column.id)" original />
                      </th>
                      <th class="sticky-top"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="tournament in filteredTournaments"
                      :key="tournament.id"
                    >
                      <td class="h-pr-2">
                        <TextBlock :size="0.75">{{
                          tournament.title
                        }}</TextBlock>
                      </td>
                      <td class="px-2">
                        <TextBlock :size="0.75">{{
                          getGamePropById(tournament.game, 'name')
                        }}</TextBlock>
                      </td>
                      <td class="px-2">
                        <TextBlock :size="0.75">{{
                          mapPlatforms(tournament) | separatedWithComma
                        }}</TextBlock>
                      </td>
                      <td class="px-2">
                        <TextBlock :size="0.75">{{
                          $t(
                            `_web_tournament-registration-options-${
                              tournament.registration_type
                            }`,
                            tournament.registration_type
                          )
                        }}</TextBlock>
                      </td>
                      <td class="px-2">
                        <TextBlock :size="0.75">{{
                          distinctTournamentsStatus[tournament.status]
                        }}</TextBlock>
                      </td>
                      <td class="px-2">
                        <TextBlock :size="0.75">{{
                          `${tournament.squads_joined}/${tournament.squads}`
                        }}</TextBlock>
                      </td>
                      <td class="px-2">
                        <TextBlock :size="0.75">{{
                          tournament.registration_start | localDate
                        }}</TextBlock>
                      </td>
                      <td class="px-2">
                        <TextBlock :size="0.75">{{
                          tournament.tournament_start | localDate
                        }}</TextBlock>
                      </td>
                      <td>
                        <HeraFormBtn
                          :class="{ 'is-loading': isLoading }"
                          class="btn-primary px-3"
                          @click.stop="onInvite(tournament.id)"
                          >{{
                            $t('_web_invite_list_invite', 'Invite')
                          }}</HeraFormBtn
                        >
                      </td>
                    </tr>
                  </tbody>
                </template>
              </table>
            </infiniteScroll>
          </div>
        </div>
      </div>
    </Panel>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import Title from '@/components/atoms/Title';
import Panel from '@/components/atoms/Panel';
import TextBlock from '@/components/atoms/TextBlock';
import infiniteScroll from '@/components/common/InfiniteScroll';
import SearchInput from '@/components/form/HeraFormSearch';
import Icon from '@/components/atoms/Icon';
import HeraFormBtn from '@/components/atoms/HeraFormBtn';
import listMixin from '@/mixins/lists';

const tournamentColumns = [
  { id: 'name', label: 'Name' },
  { id: 'game', label: 'Game' },
  { id: 'platforms', label: 'Platforms' },
  { id: 'type', label: 'Type' },
  { id: 'status', label: 'Status' },
  { id: 'squads_joined', label: 'Participants' },
  { id: 'registration_start', label: 'Registration' },
  { id: 'tournament_start', label: 'Start' },
];

const DEFAULT_STATUSES_CAN_INVITE = ['announced', 'registration_opened'];
export default {
  name: 'TournamentsListForInvites',
  components: {
    Title,
    Panel,
    TextBlock,
    SearchInput,
    infiniteScroll,
    Icon,
    HeraFormBtn,
  },
  mixins: [...listMixin],
  props: {
    player: {
      type: Object,
      default: () => {},
    },
    playerType: {
      type: String,
      default: 'profile',
    },
  },
  data() {
    return {
      tournamentSearchString: '',
      columns: tournamentColumns,
      tournaments: [],
      sorting: {
        columnId: '',
        desc: false,
      },
    };
  },
  computed: {
    ...mapGetters([
      'platforms',
      'games',
      'getGamePropById',
      'getPlatformPropById',
      'currentTournamentID',
    ]),
    filteredTournaments() {
      return this.tournaments.filter(el => el.id !== this.currentTournamentID);
    },
    distinctTournamentsStatus() {
      return {
        announced: this.$t(
          '_web_tournaments-list_status_announced',
          'Announced'
        ),
        registration_opened: this.$t(
          '_web_tournaments-list_status_registration_opened',
          'Registration open'
        ),
      };
    },
  },
  created() {
    this.fetchGames();
    this.fetchPlatforms();
    this.fetchTournaments();
  },
  methods: {
    ...mapActions([
      'getAdminTournaments',
      'fetchGames',
      'fetchPlatforms',
      'errorNotify',
      'sendPrivateInvite',
    ]),
    columnIconName(columnId) {
      return this.sorting.columnId !== columnId
        ? 'sort'
        : this.sorting.desc
        ? 'sort-dsc'
        : 'sort-asc';
    },
    mapPlatforms({ platforms }) {
      if (!platforms) {
        return [];
      }
      return platforms.map(id => this.getPlatformPropById(id, 'name'));
    },
    onFilterChange() {
      this.$nextTick(() => {
        this.tournaments = [];
        this.nextPageToken = undefined;
        this.fetchTournaments();
      });
    },
    mapFilters() {
      let order_by = this.sorting.columnId || null;
      order_by = order_by && this.sorting.desc ? order_by + ':desc' : order_by;
      return {
        page: this.nextPageToken,
        order_by,
        name: this.tournamentSearchString,
        status: DEFAULT_STATUSES_CAN_INVITE,
        player_type: this.playerType,
      };
    },
    fetchTournaments() {
      return this.getNextPageData(
        'tournaments',
        this.getAdminTournaments(this.mapFilters())
      );
    },
    onInvite(tournamentID) {
      this.setLoading();
      this.sendPrivateInvite({ ...this.player, tournamentID })
        .catch(this.errorNotify)
        .finally(this.unsetLoading);
    },
    sort(columnId) {
      if (this.sorting.columnId === columnId) {
        this.sorting.desc = !this.sorting.desc;
      } else {
        this.sorting.columnId = columnId;
        this.sorting.desc = false;
      }
      this.onFilterChange();
    },
  },
};
</script>

<style lang="scss">
.modal-opened {
  .modal-content {
    &.modal-content--wide {
      max-width: 80%;
    }
    &.modal-content--has-scroll {
      flex-grow: 0;
      flex-shrink: 1;
      max-height: 80vh;
      margin-top: 10vh;
    }
  }
}
.modal-close {
  top: -2rem;
  @include auto-rtl((right, -2.25rem));
}
.shrink-to-parent-height {
  flex-shrink: 1;
  min-height: 0;
}
.suggested-tournaments {
  &--invites {
    max-height: 80vh;
    .card-body {
      display: flex;
      flex-direction: column;
      height: 100%;
      @extend .shrink-to-parent-height;
    }
    .tournament-list-wrapper {
      overflow-y: auto;
      min-height: 0;
      table {
        thead {
          box-shadow: none;
          th {
            background: $ph-card;
            z-index: 1001;
            /*box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);*/
          }
        }
      }
    }
  }
}
</style>
